import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Layout, Sidebar } from 'react-admin';
import AppBar from './AppBar';
import Menu from './Menu';
import { getThemeDefinition } from './themes';
import { AppState } from '../types';

const SIDEBAR_MIN_WIDTH = 280;
const SIDEBAR_MAX_WIDTH = 720;
const SIDEBAR_WIDTH_KEY = 'sprive-admin-sidebar-width';

const getStoredWidth = () => {
    try {
        const stored = Number(localStorage.getItem(SIDEBAR_WIDTH_KEY));
        if (
            Number.isFinite(stored) &&
            stored >= SIDEBAR_MIN_WIDTH &&
            stored <= SIDEBAR_MAX_WIDTH
        ) {
            return stored;
        }
    } catch {
        // ignore storage errors
    }
    return SIDEBAR_MIN_WIDTH;
};

const CustomSidebar = (props: any) => (
    <Sidebar
        {...props}
        size={SIDEBAR_MIN_WIDTH}
        closedSize={0}
        style={{
            overflowX: 'hidden',
            overflowY: 'hidden',
        }}
    />
);

export default (props: any) => {
    const themeName = useSelector((state: AppState) => state.theme);
    const themeDef = getThemeDefinition(themeName);
    const theme = themeDef.theme;
    const sidebarOpen = useSelector((state: AppState) => state.admin.ui.sidebarOpen);
    const [sidebarWidth, setSidebarWidth] = useState(getStoredWidth);
    const [isResizing, setIsResizing] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const dragStartX = useRef(0);
    const dragStartWidth = useRef(SIDEBAR_MIN_WIDTH);
    const prevOpenRef = useRef(sidebarOpen);

    const clampWidth = useCallback((width: number) => {
        return Math.min(SIDEBAR_MAX_WIDTH, Math.max(SIDEBAR_MIN_WIDTH, width));
    }, []);

    const setWidthCss = useCallback((width: number) => {
        if (wrapperRef.current) {
            wrapperRef.current.style.setProperty('--sidebar-width', `${width}px`);
        }
    }, []);

    // Animate width only on open/close toggle — not while resizing
    useEffect(() => {
        if (prevOpenRef.current === sidebarOpen) return undefined;
        prevOpenRef.current = sidebarOpen;
        setIsAnimating(true);
        const timer = window.setTimeout(() => setIsAnimating(false), 450);
        return () => window.clearTimeout(timer);
    }, [sidebarOpen]);

    const handleResizeStart = useCallback(
        (event: React.MouseEvent) => {
            if (!sidebarOpen) return;
            event.preventDefault();
            dragStartX.current = event.clientX;
            dragStartWidth.current = sidebarWidth;
            // Apply before React re-render so width never eases during drag
            wrapperRef.current?.classList.add('app-sidebar-resizing');
            setIsResizing(true);
        },
        [sidebarOpen, sidebarWidth]
    );

    useEffect(() => {
        if (!isResizing) return undefined;

        const handleMouseMove = (event: MouseEvent) => {
            const nextWidth = clampWidth(
                dragStartWidth.current + (event.clientX - dragStartX.current)
            );
            setWidthCss(nextWidth);
            setSidebarWidth(nextWidth);
        };

        const handleMouseUp = () => {
            wrapperRef.current?.classList.remove('app-sidebar-resizing');
            setIsResizing(false);
            setSidebarWidth((current) => {
                const width = clampWidth(current);
                setWidthCss(width);
                try {
                    localStorage.setItem(SIDEBAR_WIDTH_KEY, String(width));
                } catch {
                    // ignore storage errors
                }
                return width;
            });
        };

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [clampWidth, isResizing, setWidthCss]);

    const wrapperClass = [
        sidebarOpen ? 'app-sidebar-open' : 'app-sidebar-closed',
        `app-theme-${themeDef.name}`,
        `app-theme-mode-${themeDef.mode}`,
        isResizing ? 'app-sidebar-resizing' : '',
        isAnimating ? 'app-sidebar-animating' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div
            ref={wrapperRef}
            className={wrapperClass}
            style={
                {
                    '--sidebar-width': `${sidebarWidth}px`,
                    ...themeDef.cssVars,
                } as React.CSSProperties
            }
        >
            <Layout
                {...props}
                appBar={AppBar}
                sidebar={CustomSidebar}
                menu={Menu}
                theme={theme}
            />
            {sidebarOpen && !isAnimating && (
                <div
                    className="sidebar-resize-handle"
                    onMouseDown={handleResizeStart}
                    role="separator"
                    aria-orientation="vertical"
                    aria-label="Resize sidebar"
                    title="Drag to resize sidebar"
                />
            )}
        </div>
    );
};
